import LegalPageLayout from "@/components/LegalPageLayout";
import { COMPANY } from "@shared/company-info";

export default function Privacidade() {
  return (
    <LegalPageLayout
      title="Política de Privacidade"
      subtitle="Como a Lead Conversa coleta, usa, compartilha e protege seus dados pessoais, em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018 — LGPD)."
      lastUpdated={COMPANY.lastUpdatedLegal}
    >
      <h2>1. Quem somos</h2>
      <p>
        A <strong>Lead Conversa</strong> é uma plataforma de conversação, CRM, marketing por e-mail
        e mensageria via WhatsApp, oferecida como software como serviço (SaaS) por{" "}
        <strong>{COMPANY.legalEntity}</strong>, inscrita no CNPJ sob o nº {COMPANY.cnpj}, com sede
        em {COMPANY.addressFormatted} ("<strong>nós</strong>", "<strong>nosso</strong>", "
        <strong>Controladora</strong>").
      </p>
      <p>
        Esta Política descreve o tratamento de dados pessoais realizado pela Lead Conversa tanto
        em relação aos usuários do nosso site e da nossa plataforma, quanto em relação aos dados
        que nossos clientes (tenants) carregam na plataforma para executar suas próprias
        campanhas e atendimentos.
      </p>

      <h2>2. Encarregado pelo Tratamento de Dados (DPO)</h2>
      <p>
        Você pode entrar em contato com nosso Encarregado pelo Tratamento de Dados Pessoais a
        qualquer momento:
      </p>
      <ul>
        <li>
          E-mail: <a href={`mailto:${COMPANY.dpoEmail}`}>{COMPANY.dpoEmail}</a>
        </li>
        <li>Endereço postal: {COMPANY.addressFormatted}</li>
      </ul>

      <h2>3. Dados que coletamos</h2>

      <h3>3.1. Dados de clientes da plataforma (tenants)</h3>
      <p>Quando uma empresa contrata a Lead Conversa, coletamos:</p>
      <ul>
        <li>
          <strong>Dados de cadastro:</strong> nome, e-mail, telefone, cargo, nome da empresa,
          CNPJ/CPF, endereço e demais informações fornecidas no onboarding.
        </li>
        <li>
          <strong>Dados de pagamento:</strong> processados por prestadores especializados; não
          armazenamos números completos de cartão de crédito em nossos servidores.
        </li>
        <li>
          <strong>Dados de uso da plataforma:</strong> registros de acesso, endereço IP, páginas
          visitadas, ações executadas e logs técnicos necessários para segurança e auditoria.
        </li>
      </ul>

      <h3>3.2. Dados de contatos finais (titulares dos clientes)</h3>
      <p>
        Os clientes da Lead Conversa (tenants) importam, de forma autônoma, listas de contatos
        de seus próprios titulares — leads, clientes, usuários finais. Nesse tratamento, a Lead
        Conversa atua como <strong>Operadora</strong> de dados pessoais, nos termos do art. 5º,
        VII, da LGPD, processando informações estritamente conforme as instruções do Controlador
        (tenant). Tais dados podem incluir:
      </p>
      <ul>
        <li>Nome, e-mail, telefone, tags e campos customizados cadastrados pelo tenant.</li>
        <li>
          Histórico de mensagens trocadas por WhatsApp e e-mail, cliques em links, aberturas,
          descadastros e interações.
        </li>
      </ul>
      <p>
        A legalidade do tratamento desses dados (consentimento, legítimo interesse, execução de
        contrato etc.) é responsabilidade do cliente contratante.
      </p>

      <h3>3.3. Dados de navegação no site</h3>
      <p>
        Coletamos cookies e identificadores de dispositivo para autenticação, medição de
        desempenho e melhoria da experiência.
      </p>

      <h3>3.4. Assistente de reuniões (extensão para navegador)</h3>
      <p>
        A Lead Conversa oferece, como recurso opcional, uma extensão para o navegador que o
        usuário instala e liga por conta própria durante uma reunião por vídeo. Enquanto estiver
        ligada, a extensão captura o áudio da aba da reunião e o áudio do microfone do próprio
        usuário e o transmite, de forma cifrada, aos servidores da Lead Conversa, exclusivamente
        para transcrição em tempo real e apoio ao usuário durante a conversa.
      </p>
      <ul>
        <li>
          <strong>O áudio não é gravado nem armazenado.</strong> Ele é processado em fluxo e
          descartado; apenas a transcrição em texto e as anotações geradas ficam guardadas no
          workspace da empresa do usuário, sob as mesmas regras de acesso da plataforma.
        </li>
        <li>
          A captura só acontece quando o usuário a liga explicitamente, e é encerrada quando ele
          a desliga, fecha a aba ou encerra a reunião. O ícone da extensão sinaliza quando a
          captura está ativa.
        </li>
        <li>
          A extensão guarda localmente um token de acesso à conta do usuário, que pode ser
          removido a qualquer momento nas configurações da plataforma.
        </li>
        <li>
          É responsabilidade do usuário informar os demais participantes da reunião sobre a
          transcrição, conforme a legislação aplicável.
        </li>
      </ul>

      <h3>3.5. Dados recebidos das plataformas da Meta</h3>
      <p>
        Quando um cliente conecta uma conta do Facebook, do Instagram ou do WhatsApp Business à
        Lead Conversa (pelo botão "Entrar com Facebook" ou "Conectar WhatsApp"), recebemos da
        Meta Platforms, Inc. os identificadores da conta, da página, do número e do portfólio de
        negócios, um token de acesso e, no caso do WhatsApp, as mensagens, os contatos e o
        histórico de conversas do número conectado. Usamos esses dados exclusivamente para
        prestar o serviço contratado (receber e enviar mensagens, gerir modelos e anúncios em
        nome do cliente). Os tokens são armazenados cifrados e podem ser revogados a qualquer
        momento pelo cliente, na plataforma ou nas configurações da própria Meta. O uso está
        sujeito às Políticas da Plataforma Meta. Instruções para exclusão desses dados:{" "}
        <a href="/exclusao-de-dados">Exclusão de dados</a>.
      </p>

      <h2>4. Finalidades e bases legais</h2>
      <p>
        Tratamos dados pessoais com base nas seguintes hipóteses legais previstas no art. 7º e
        art. 11 da LGPD:
      </p>
      <ul>
        <li>
          <strong>Execução de contrato</strong> (art. 7º, V): prestação dos serviços
          contratados, autenticação, cobrança, suporte.
        </li>
        <li>
          <strong>Cumprimento de obrigação legal ou regulatória</strong> (art. 7º, II): emissão
          fiscal, retenção de logs (Marco Civil da Internet).
        </li>
        <li>
          <strong>Consentimento</strong> (art. 7º, I): comunicações de marketing da Lead
          Conversa, cookies não essenciais.
        </li>
        <li>
          <strong>Legítimo interesse</strong> (art. 7º, IX): prevenção de fraude, segurança,
          melhoria do serviço, em análise de proporcionalidade com seus direitos.
        </li>
      </ul>

      <h2>5. Com quem compartilhamos seus dados</h2>
      <p>
        Compartilhamos dados pessoais apenas com prestadores de serviço (operadores) essenciais
        para o funcionamento da plataforma, contratualmente obrigados a proteger seus dados:
      </p>
      <ul>
        <li>
          <strong>Provedores de envio de e-mail</strong> — envio transacional e de campanhas.
        </li>
        <li>
          <strong>WhatsApp Business API / Meta Platforms</strong> — envio e recepção de mensagens.
        </li>
        <li>
          <strong>Provedores de hospedagem em nuvem</strong> — armazenamento e processamento.
        </li>
        <li>
          <strong>Provedores de transcrição de voz e de inteligência artificial</strong> — para
          os recursos de IA da plataforma, inclusive o assistente de reuniões (item 3.4), sem
          retenção do conteúdo por esses provedores para fins próprios.
        </li>
        <li>
          <strong>Processadores de pagamento</strong> — cobrança de assinaturas.
        </li>
        <li>
          <strong>Autoridades públicas</strong> — quando exigido por lei, ordem judicial ou
          processo regulatório.
        </li>
      </ul>
      <p>
        <strong>Não vendemos dados pessoais</strong> e não compartilhamos dados com terceiros
        para fins de publicidade de outras empresas.
      </p>

      <h2>6. Transferência internacional</h2>
      <p>
        Alguns de nossos prestadores de serviço estão localizados fora do Brasil (em especial nos
        Estados Unidos). Nessas hipóteses, as transferências são realizadas em conformidade
        com o art. 33 da LGPD, mediante cláusulas contratuais específicas e/ou outras
        salvaguardas adequadas.
      </p>

      <h2>7. Retenção de dados</h2>
      <p>
        Retemos dados pessoais pelo tempo necessário ao cumprimento das finalidades informadas e
        das obrigações legais aplicáveis, em especial:
      </p>
      <ul>
        <li>Logs de acesso: no mínimo 6 meses (Marco Civil da Internet).</li>
        <li>Dados fiscais e contábeis: no mínimo 5 anos.</li>
        <li>
          Dados de contatos e conteúdo de campanhas: enquanto durar a relação contratual com o
          tenant, podendo ser excluídos por solicitação, respeitados prazos legais.
        </li>
      </ul>

      <h2>8. Direitos do titular</h2>
      <p>
        Nos termos do art. 18 da LGPD, você pode, a qualquer momento, solicitar:
      </p>
      <ul>
        <li>Confirmação da existência de tratamento;</li>
        <li>Acesso aos seus dados;</li>
        <li>Correção de dados incompletos, inexatos ou desatualizados;</li>
        <li>
          Anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados
          em desconformidade com a LGPD;
        </li>
        <li>Portabilidade;</li>
        <li>Eliminação dos dados tratados com base em consentimento;</li>
        <li>Informação sobre compartilhamentos;</li>
        <li>Revogação do consentimento.</li>
      </ul>
      <p>
        Se você é titular de dados processados por um cliente da Lead Conversa, recomendamos
        contatar primeiro o respectivo Controlador. Ainda assim, podemos encaminhar sua
        solicitação por meio de{" "}
        <a href={`mailto:${COMPANY.dpoEmail}`}>{COMPANY.dpoEmail}</a>.
      </p>

      <h2>9. Segurança da informação</h2>
      <p>
        Aplicamos medidas técnicas e organizacionais razoáveis para proteger os dados pessoais,
        incluindo criptografia em trânsito (TLS), controles de acesso, segregação de ambientes,
        registros de auditoria e processos de resposta a incidentes. Nenhum sistema é 100% seguro,
        mas nos comprometemos com o estado da arte e com a notificação de incidentes relevantes
        conforme exigido pela ANPD.
      </p>

      <h2>10. Uso por menores de idade</h2>
      <p>
        A Lead Conversa é destinada a uso empresarial e não é dirigida a crianças e adolescentes
        menores de 18 anos. Não coletamos conscientemente dados de menores.
      </p>

      <h2>11. Cookies</h2>
      <p>
        Utilizamos cookies essenciais para autenticação e funcionamento do serviço, e cookies de
        medição para análise de uso. Você pode gerenciar preferências diretamente no seu
        navegador.
      </p>

      <h2>12. Atualizações desta Política</h2>
      <p>
        Podemos atualizar esta Política periodicamente. Mudanças materiais serão comunicadas por
        e-mail ou por aviso destacado na plataforma. A data da última atualização está indicada
        no topo deste documento.
      </p>

      <h2>13. Contato</h2>
      <p>
        Dúvidas, reclamações ou solicitações relacionadas a esta Política de Privacidade podem
        ser enviadas para{" "}
        <a href={`mailto:${COMPANY.dpoEmail}`}>{COMPANY.dpoEmail}</a> ou pelo endereço postal
        indicado acima. Você também tem o direito de apresentar reclamação à Autoridade Nacional
        de Proteção de Dados (ANPD).
      </p>
    </LegalPageLayout>
  );
}
